import { useState, useEffect, useCallback } from "react";
import { apiClient, useAuth } from "../App";
import Layout from "../components/Layout";
import { todayLocalISO, daysAgoLocalISO } from "../lib/utils";
import { SortableHeader, toggleSort, applySortFn, getPersistedSort } from "../components/SortableHeader";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger,
} from "../components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Plus, DollarSign, Filter, Calendar, ChevronLeft, ChevronRight, BarChart3, UserX, Layers, AlertTriangle, Wand2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import PayGroupSummarySheet from "../components/PayGroupSummarySheet";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";

const Payments = () => {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [payments, setPayments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddSheet, setShowAddSheet] = useState(false);
  const [showSummarySheet, setShowSummarySheet] = useState(false);
  // Snap-periods migration UX (pre + post dialogs)
  const [showSnapConfirm, setShowSnapConfirm] = useState(false);
  const [snapRunning, setSnapRunning] = useState(false);
  const [snapResult, setSnapResult] = useState(null);  // {snapped, flagged, already_aligned, examples_snapped, examples_flagged}
  const [filterReviewOnly, setFilterReviewOnly] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filterEmployee, setFilterEmployee] = useState("all");
  const [sort, setSort] = useState(() => getPersistedSort("payments", "payment_date", "desc"));
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [monthlySummary, setMonthlySummary] = useState(null);
  // Pay-period dropdown state — fetched per-employee from /employees/{id}/pay-periods.
  // We also support a "custom" escape hatch for off-cycle payments (bonuses,
  // advances, error corrections) — when picked, we fall back to the two
  // free-form date pickers.
  const [payPeriods, setPayPeriods] = useState([]);
  const [periodSelectValue, setPeriodSelectValue] = useState(""); // value of the dropdown
  const [periodsLoading, setPeriodsLoading] = useState(false);
  const [showOlderPeriods, setShowOlderPeriods] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  const [formData, setFormData] = useState({
    employee_id: "",
    pay_period_start: "",
    pay_period_end: "",
    payment_date: todayLocalISO(),
    payment_method: "cash",
    amount_paid: "",
    extra_amount: "0",
    extra_reason: "",
    deduction_amount: "0",
    deduction_reason: "",
    notes: ""
  });

  const monthStart = `${selectedMonth}-01`;
  const monthEnd = (() => {
    const [y, m] = selectedMonth.split('-').map(Number);
    const d = new Date(y, m, 0);
    return `${selectedMonth}-${String(d.getDate()).padStart(2, '0')}`;
  })();

  const navigateMonth = (dir) => {
    const [y, m] = selectedMonth.split('-').map(Number);
    const d = new Date(y, m - 1 + dir, 1);
    setSelectedMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const monthLabel = (() => {
    const [y, m] = selectedMonth.split('-').map(Number);
    return new Date(y, m - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  })();

  const fetchData = useCallback(async () => {
    try {
      // When the user is filtering for flagged-only, ignore the month scope —
      // flagged payments can be scattered across any month and a month-scoped
      // query produces a confusing empty list (bug fix Feb 2026).
      const params = [];
      if (!filterReviewOnly) {
        params.push(`start_date=${monthStart}`, `end_date=${monthEnd}`);
      }
      if (filterEmployee !== 'all') params.push(`employee_id=${filterEmployee}`);
      if (filterReviewOnly) params.push("flagged_only=true");
      const url = params.length ? `/payments?${params.join('&')}` : "/payments";
      const [payRes, empRes] = await Promise.all([
        apiClient.get(url),
        apiClient.get('/employees')
      ]);
      setPayments(payRes.data.payments);
      setEmployees(empRes.data.employees);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  }, [filterEmployee, monthStart, monthEnd, filterReviewOnly]);

  const fetchMonthlySummary = useCallback(async () => {
    try {
      const res = await apiClient.get(`/payments/monthly-summary?month=${selectedMonth}`);
      setMonthlySummary(res.data);
    } catch { /* silent */ }
  }, [selectedMonth]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchMonthlySummary(); }, [fetchMonthlySummary]);

  const handleEmployeeSelect = async (empId) => {
    setFormData({...formData, employee_id: empId});
    const emp = employees.find(e => e.employee_id === empId);
    setSelectedEmployee(emp);

    // Fetch real pay periods for this employee (deterministic from pay group).
    setPeriodsLoading(true);
    setPayPeriods([]);
    setPeriodSelectValue("");
    try {
      const res = await apiClient.get(`/employees/${empId}/pay-periods`);
      const periods = res.data.periods || [];
      setPayPeriods(periods);

      // Pre-select the most recent unpaid period — saves a click in the
      // common case (owner paying for the period that just ended).
      const unpaid = [...periods].reverse().find(p => p.balance > 0.01);
      const target = unpaid || periods[periods.length - 1];
      if (target) {
        const key = `${target.period_start}|${target.period_end}`;
        setPeriodSelectValue(key);
        setFormData(prev => ({
          ...prev,
          employee_id: empId,
          pay_period_start: target.period_start,
          pay_period_end: target.period_end,
          // Pre-fill amount = period balance (unpaid amount). Owner can override.
          amount_paid: unpaid ? String(unpaid.balance) : prev.amount_paid,
        }));
        return;
      }
    } catch (err) {
      console.warn("Could not load pay periods, falling back to date pickers:", err);
    } finally {
      setPeriodsLoading(false);
    }

    // Fallback: legacy 2-week auto-suggest if periods endpoint failed.
    setPeriodSelectValue("__custom__");
    setFormData(prev => ({
      ...prev,
      employee_id: empId,
      pay_period_start: daysAgoLocalISO(14),
      pay_period_end: todayLocalISO(),
    }));
  };

  const handlePeriodSelect = (value) => {
    setPeriodSelectValue(value);
    if (value === "__custom__") {
      // Don't reset — let owner edit. If empty, prefill last 2 weeks.
      if (!formData.pay_period_start || !formData.pay_period_end) {
        setFormData(prev => ({
          ...prev,
          pay_period_start: daysAgoLocalISO(14),
          pay_period_end: todayLocalISO(),
        }));
      }
      return;
    }
    const [ps, pe] = value.split("|");
    const period = payPeriods.find(p => p.period_start === ps && p.period_end === pe);
    setFormData(prev => ({
      ...prev,
      pay_period_start: ps,
      pay_period_end: pe,
      // Auto-fill the unpaid amount when the owner picks a period — they can
      // still override (partial payments, advances, etc.).
      amount_paid: period && period.balance > 0 ? String(period.balance) : prev.amount_paid,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiClient.post('/payments', {
        employee_id: formData.employee_id,
        pay_period_start: formData.pay_period_start,
        pay_period_end: formData.pay_period_end,
        payment_date: formData.payment_date,
        payment_method: formData.payment_method,
        amount_paid: parseFloat(formData.amount_paid),
        extra_amount: parseFloat(formData.extra_amount || 0),
        extra_reason: formData.extra_reason || undefined,
        deduction_amount: parseFloat(formData.deduction_amount || 0),
        deduction_reason: formData.deduction_reason || undefined,
        notes: formData.notes || undefined
      });
      
      setShowAddSheet(false);
      setSelectedEmployee(null);
      setPayPeriods([]);
      setPeriodSelectValue("");
      setShowOlderPeriods(false);
      setFormData({
        employee_id: "",
        pay_period_start: "",
        pay_period_end: "",
        payment_date: todayLocalISO(),
        payment_method: "cash",
        amount_paid: "",
        extra_amount: "0",
        extra_reason: "",
        deduction_amount: "0",
        deduction_reason: "",
        notes: ""
      });
      fetchData();
    } catch (error) {
      console.error("Failed to record payment:", error);
      toast.error(error.response?.data?.detail || "Failed to record payment");
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const dateOnly = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
    return new Date(dateOnly + 'T12:00:00').toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getEmployeeName = (id) => {
    const emp = employees.find(e => e.employee_id === id);
    return emp?.name || "Unknown";
  };

  const isTerminated = (id) => {
    const emp = employees.find(e => e.employee_id === id);
    return !!emp?.termination_date;
  };

  const handleRunSnapPeriods = async () => {
    setSnapRunning(true);
    try {
      const res = await apiClient.post("/payroll/snap-misaligned-periods");
      setSnapResult(res.data);
      setShowSnapConfirm(false);
      fetchData();
      fetchFlaggedCount();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Migration failed");
    } finally {
      setSnapRunning(false);
    }
  };

  // Inline edit-period for a flagged payment (Feb 2026 bug fix — let users
  // fix the misaligned period right from the flagged list instead of hunting
  // for each payment in the employee detail page).
  const [editPeriodTarget, setEditPeriodTarget] = useState(null);  // payment row
  const [editPeriodOptions, setEditPeriodOptions] = useState([]);
  const [editPeriodLoading, setEditPeriodLoading] = useState(false);
  const [editPeriodSaving, setEditPeriodSaving] = useState(false);
  const [editPeriodValue, setEditPeriodValue] = useState("");  // "start|end"

  const openEditPeriod = async (payment) => {
    setEditPeriodTarget(payment);
    setEditPeriodOptions([]);
    setEditPeriodValue("");
    setEditPeriodLoading(true);
    try {
      const res = await apiClient.get(`/employees/${payment.employee_id}/pay-periods`);
      const periods = res.data.periods || [];
      setEditPeriodOptions(periods);
      // Pre-select the period whose date range contains the payment_date.
      const match = [...periods].reverse().find(
        (p) => p.period_start <= payment.payment_date && payment.payment_date <= p.period_end
      ) || periods[periods.length - 1];
      if (match) setEditPeriodValue(`${match.period_start}|${match.period_end}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load pay periods");
    } finally {
      setEditPeriodLoading(false);
    }
  };

  const handleSaveEditPeriod = async () => {
    if (!editPeriodTarget || !editPeriodValue) return;
    const [ps, pe] = editPeriodValue.split("|");
    setEditPeriodSaving(true);
    try {
      await apiClient.put(`/payments/${editPeriodTarget.payment_id}`, {
        pay_period_start: ps,
        pay_period_end: pe,
      });
      toast.success("Pay period updated · flag cleared");
      setEditPeriodTarget(null);
      fetchData();
      fetchFlaggedCount();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update period");
    } finally {
      setEditPeriodSaving(false);
    }
  };

  const handleClearReviewFlag = async (paymentId) => {
    try {
      await apiClient.post(`/payments/${paymentId}/clear-review-flag`);
      toast.success("Marked as reviewed");
      fetchData();
      fetchFlaggedCount();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to clear flag");
    }
  };

  // Tenant-wide count of flagged payments (independent of month scope).
  const [tenantFlaggedCount, setTenantFlaggedCount] = useState(0);

  const fetchFlaggedCount = useCallback(async () => {
    try {
      const res = await apiClient.get("/payments/flagged-count");
      setTenantFlaggedCount(res.data.count || 0);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchFlaggedCount(); }, [fetchFlaggedCount]);

  const flaggedCount = tenantFlaggedCount;
  const visiblePayments = payments;

  const exportToPDF = () => {
    // Browser-based PDF export using print
    const printContent = `
      <html>
        <head>
          <title>Payment Summary</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { color: #0A192F; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 12px; text-align: left; }
            th { background-color: #f8fafc; }
            .total { font-weight: bold; }
          </style>
        </head>
        <body>
          <h1>Payment Summary</h1>
          <p>Generated: ${new Date().toLocaleDateString()}</p>
          <table>
            <thead>
              <tr>
                <th>Employee</th>
                <th>Date</th>
                <th>Period</th>
                <th>Method</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              ${payments.map(p => `
                <tr>
                  <td>${getEmployeeName(p.employee_id)}</td>
                  <td>${formatDate(p.payment_date)}</td>
                  <td>${formatDate(p.pay_period_start)} - ${formatDate(p.pay_period_end)}</td>
                  <td>${p.payment_method}</td>
                  <td>${formatCurrency(p.amount_paid)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <p class="total">Total: ${formatCurrency(payments.reduce((sum, p) => sum + p.amount_paid, 0))}</p>
        </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  // Chart data
  const costChartData = monthlySummary?.cost_per_day?.filter(d => d.cost > 0).map(d => ({
    date: new Date(d.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    cost: d.cost,
  })) || [];

  const empChartData = monthlySummary?.employee_summary?.filter(e => e.paid > 0 || e.owed > 0) || [];

  return (
    <Layout>
      <div data-testid="payments-page" className="max-w-7xl mx-auto px-4 py-8 space-y-6">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#0A192F] font-['Manrope'] tracking-tight">
              Payments
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Record and track employee payments
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => navigateMonth(-1)} data-testid="pay-prev-month">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm font-semibold text-[#0A192F] min-w-[140px] text-center" data-testid="pay-month-label">{monthLabel}</span>
            <Button variant="outline" size="sm" onClick={() => navigateMonth(1)} data-testid="pay-next-month">
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowSummarySheet(true)}
              data-testid="pay-group-summary-btn"
            >
              <Layers className="w-4 h-4 mr-2" />
              Pay Group Summary
            </Button>
            <Button 
              variant="outline" 
              onClick={exportToPDF}
              data-testid="export-pdf-btn"
            >
              Export PDF
            </Button>
            {isOwner && (
              <Button
                variant="outline"
                onClick={() => setShowSnapConfirm(true)}
                data-testid="snap-periods-btn"
                className="text-xs"
                title="One-time fix: align existing payments to canonical pay periods"
              >
                <Wand2 className="w-4 h-4 mr-1.5" /> Snap periods
              </Button>
            )}
            <Sheet open={showAddSheet} onOpenChange={setShowAddSheet}>
              <SheetTrigger asChild>
                <Button 
                  className="bg-[#0D9488] hover:bg-[#14B8A6]"
                  data-testid="quick-pay-button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Quick Pay
                </Button>
              </SheetTrigger>
              <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Record Payment</SheetTitle>
                  <SheetDescription>
                    Record a payment for an employee. Quick pay in under 3 clicks!
                  </SheetDescription>
                </SheetHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-6">
                  <div className="space-y-2">
                    <Label>Employee *</Label>
                    <Select 
                      value={formData.employee_id} 
                      onValueChange={handleEmployeeSelect}
                    >
                      <SelectTrigger data-testid="payment-employee-select">
                        <SelectValue placeholder="Select employee" />
                      </SelectTrigger>
                      <SelectContent>
                        {employees.map(emp => (
                          <SelectItem key={emp.employee_id} value={emp.employee_id}>
                            <div className="flex items-center justify-between w-full">
                              <span>{emp.name}</span>
                              {emp.balance?.balance > 0 && (
                                <span className="text-[#E11D48] text-sm ml-2">
                                  {formatCurrency(emp.balance.balance)}
                                </span>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedEmployee && (
                    <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                      <div className="flex justify-between">
                        <span className="text-slate-600">Outstanding Balance:</span>
                        <span className={`font-semibold ${
                          selectedEmployee.balance?.balance > 0 ? 'text-[#E11D48]' : 'text-[#10B981]'
                        }`}>
                          {formatCurrency(selectedEmployee.balance?.balance || 0)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Pay Type:</span>
                        <span className="capitalize">{selectedEmployee.pay_type}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Rate:</span>
                        <span>{formatCurrency(selectedEmployee.pay_value)}</span>
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Pay Period *</Label>
                    {periodsLoading ? (
                      <div className="text-xs text-slate-500 py-2">Loading available periods…</div>
                    ) : payPeriods.length > 0 ? (
                      <Select value={periodSelectValue} onValueChange={handlePeriodSelect}>
                        <SelectTrigger data-testid="payment-period-select">
                          <SelectValue placeholder="Select a pay period" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[320px]">
                          {(() => {
                            // Newest periods first; "older paid" hidden by default to keep the list short.
                            const reversed = [...payPeriods].reverse();
                            const visible = showOlderPeriods
                              ? reversed
                              : reversed.filter((p, idx) => idx < 24 || p.balance > 0.01);
                            return visible.map(p => {
                              const key = `${p.period_start}|${p.period_end}`;
                              const isPaid = p.balance <= 0.01;
                              const status = isPaid
                                ? "Paid"
                                : `${formatCurrency(p.balance)} unpaid`;
                              return (
                                <SelectItem key={key} value={key} data-testid={`period-option-${p.period_start}`}>
                                  <span className="flex items-center justify-between gap-3 w-full">
                                    <span className="font-medium">{formatDate(p.period_start)} – {formatDate(p.period_end)}</span>
                                    <span className={`text-xs ${isPaid ? "text-slate-400" : "text-amber-600 font-semibold"}`}>{status}</span>
                                  </span>
                                </SelectItem>
                              );
                            });
                          })()}
                          <SelectItem value="__custom__" data-testid="period-option-custom">
                            <span className="text-slate-600 italic">Other / custom dates…</span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="text-xs text-slate-500 py-2">No pay periods available — pick custom dates below.</div>
                    )}
                    {payPeriods.length > 24 && periodSelectValue !== "__custom__" && (
                      <button
                        type="button"
                        onClick={() => setShowOlderPeriods(s => !s)}
                        className="text-xs text-[#0D9488] hover:underline"
                        data-testid="toggle-older-periods-btn"
                      >
                        {showOlderPeriods ? "Hide older paid periods" : "Show older paid periods"}
                      </button>
                    )}
                  </div>

                  {/* Free-form date pickers — only shown when "Other / custom" is selected
                      OR when the periods endpoint failed and we're in fallback mode. */}
                  {(periodSelectValue === "__custom__" || (payPeriods.length === 0 && !periodsLoading && formData.employee_id)) && (
                    <div className="grid grid-cols-2 gap-4 p-3 bg-amber-50 border border-amber-100 rounded-md">
                      <div className="space-y-2">
                        <Label className="text-xs">Period Start *</Label>
                        <Input
                          type="date"
                          value={formData.pay_period_start}
                          onChange={(e) => setFormData({...formData, pay_period_start: e.target.value})}
                          required
                          data-testid="payment-period-start"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Period End *</Label>
                        <Input
                          type="date"
                          value={formData.pay_period_end}
                          onChange={(e) => setFormData({...formData, pay_period_end: e.target.value})}
                          required
                          data-testid="payment-period-end"
                        />
                      </div>
                      <p className="col-span-2 text-[11px] text-amber-700">
                        ⚠️ Custom dates may not align with your pay schedule and won&apos;t appear under their period in the Pay Periods view.
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Payment Date *</Label>
                      <Input
                        type="date"
                        value={formData.payment_date}
                        onChange={(e) => setFormData({...formData, payment_date: e.target.value})}
                        required
                        data-testid="payment-date"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Method *</Label>
                      <Select 
                        value={formData.payment_method} 
                        onValueChange={(val) => setFormData({...formData, payment_method: val})}
                      >
                        <SelectTrigger data-testid="payment-method-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cash">Cash</SelectItem>
                          <SelectItem value="zelle">Zelle</SelectItem>
                          <SelectItem value="check">Check</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Amount Paid *</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.amount_paid}
                        onChange={(e) => setFormData({...formData, amount_paid: e.target.value})}
                        placeholder="0.00"
                        className="pl-9"
                        required
                        data-testid="payment-amount"
                      />
                    </div>
                  </div>

                  <div className="border-t border-slate-200 pt-4">
                    <p className="text-sm font-medium text-slate-700 mb-3">Adjustments (Optional)</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-[#10B981]">Extra Pay</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.extra_amount}
                          onChange={(e) => setFormData({...formData, extra_amount: e.target.value})}
                          placeholder="0.00"
                          data-testid="payment-extra"
                        />
                        <Input
                          placeholder="Reason (e.g., Bonus)"
                          value={formData.extra_reason}
                          onChange={(e) => setFormData({...formData, extra_reason: e.target.value})}
                          data-testid="payment-extra-reason"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[#E11D48]">Deduction</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.deduction_amount}
                          onChange={(e) => setFormData({...formData, deduction_amount: e.target.value})}
                          placeholder="0.00"
                          data-testid="payment-deduction"
                        />
                        <Input
                          placeholder="Reason (e.g., Advance)"
                          value={formData.deduction_reason}
                          onChange={(e) => setFormData({...formData, deduction_reason: e.target.value})}
                          data-testid="payment-deduction-reason"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Notes</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      placeholder="Any additional notes..."
                      rows={2}
                      data-testid="payment-notes"
                    />
                  </div>

                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setShowAddSheet(false)}>
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      disabled={saving || !formData.employee_id || !formData.amount_paid}
                      className="bg-[#0D9488] hover:bg-[#14B8A6]"
                      data-testid="submit-payment-btn"
                    >
                      {saving ? "Recording..." : "Record Payment"}
                    </Button>
                  </div>
                </form>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {/* Summary Cards */}
        {monthlySummary && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 uppercase tracking-wider">Total Paid</p>
                <p className="text-xl font-bold text-[#10B981]">{formatCurrency(monthlySummary.total_paid)}</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 uppercase tracking-wider">Total Owed</p>
                <p className="text-xl font-bold text-[#E11D48]">{formatCurrency(monthlySummary.total_owed)}</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 uppercase tracking-wider">Net Balance</p>
                <p className={`text-xl font-bold ${monthlySummary.total_owed - monthlySummary.total_paid > 0 ? 'text-[#E11D48]' : 'text-[#10B981]'}`}>
                  {formatCurrency(monthlySummary.total_owed - monthlySummary.total_paid)}
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Charts */}
        {(empChartData.length > 0 || costChartData.length > 0) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {empChartData.length > 0 && (
              <Card className="border-slate-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-[#0A192F] flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-[#0D9488]" /> Paid vs Owed by Employee
                  </CardTitle>
                </CardHeader>
                <CardContent className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={empChartData} barGap={2}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0' }} formatter={(v) => formatCurrency(v)} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="paid" name="Paid" fill="#10B981" radius={[3, 3, 0, 0]} />
                      <Bar dataKey="owed" name="Owed" fill="#E11D48" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
            {costChartData.length > 0 && (
              <Card className="border-slate-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-[#0A192F] flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-[#0D9488]" /> Cost per Day
                  </CardTitle>
                </CardHeader>
                <CardContent className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={costChartData} barGap={2}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0' }} formatter={(v) => formatCurrency(v)} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="cost" name="Cost" fill="#0D9488" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Filters + Table */}
        <Card className="border-slate-200">
          <CardContent className="p-0">
            <div className="p-4 border-b border-slate-100 flex items-center gap-3 flex-wrap">
              <Filter className="w-4 h-4 text-slate-400" />
              <Select value={filterEmployee} onValueChange={setFilterEmployee}>
                <SelectTrigger className="w-[200px]" data-testid="payment-filter-employee">
                  <SelectValue placeholder="All Employees" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Employees</SelectItem>
                  {employees.map(emp => (
                    <SelectItem key={emp.employee_id} value={emp.employee_id}>
                      {emp.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(flaggedCount > 0 || filterReviewOnly) && (
                <button
                  type="button"
                  onClick={() => setFilterReviewOnly(v => !v)}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${
                    filterReviewOnly
                      ? "bg-amber-500 text-white border-amber-500"
                      : "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100"
                  }`}
                  data-testid="filter-review-only-btn"
                  title={filterReviewOnly ? "Click to show all payments again" : "Filter to only flagged payments"}
                >
                  <AlertTriangle className="w-3.5 h-3.5" />
                  {filterReviewOnly
                    ? `Showing ${flaggedCount} flagged · click to show all`
                    : `${flaggedCount} flagged for review`}
                </button>
              )}
              <Badge variant="outline" className="ml-auto text-slate-500">{visiblePayments.length} payments</Badge>
            </div>
            {filterReviewOnly && (
              <div className="px-4 py-2 bg-amber-50 border-b border-amber-100 text-xs text-amber-800 flex items-center justify-between" data-testid="flagged-only-banner">
                <span className="flex items-center gap-2">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Showing flagged payments across <strong>all months</strong> (month filter is ignored while this view is active).
                </span>
                <button
                  type="button"
                  onClick={() => setFilterReviewOnly(false)}
                  className="text-amber-700 hover:text-amber-900 font-medium underline"
                  data-testid="flagged-banner-exit-btn"
                >
                  Exit flagged view
                </button>
              </div>
            )}
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80 hover:bg-slate-50/80">
                  <TableHead><SortableHeader label="Employee" sortKey="_empName" sortState={sort} onSort={(k) => setSort(s => toggleSort(s, k, "payments"))} /></TableHead>
                  <TableHead><SortableHeader label="Payment Date" sortKey="payment_date" sortState={sort} onSort={(k) => setSort(s => toggleSort(s, k, "payments"))} /></TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Period</TableHead>
                  <TableHead><SortableHeader label="Method" sortKey="payment_method" sortState={sort} onSort={(k) => setSort(s => toggleSort(s, k, "payments"))} /></TableHead>
                  <TableHead><SortableHeader label="Amount" sortKey="amount_paid" sortState={sort} onSort={(k) => setSort(s => toggleSort(s, k, "payments"))} /></TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Adjustments</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 3 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={6}><div className="h-12 bg-slate-100 rounded animate-pulse"></div></TableCell>
                    </TableRow>
                  ))
                ) : visiblePayments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12">
                      <DollarSign className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                      <p className="text-slate-500">
                        {filterReviewOnly ? "No flagged payments — all clear!" : `No payments for ${monthLabel}`}
                      </p>
                      {filterReviewOnly && (
                        <button
                          type="button"
                          onClick={() => setFilterReviewOnly(false)}
                          className="mt-3 text-xs text-[#0D9488] hover:underline"
                          data-testid="empty-state-show-all-btn"
                        >
                          ← Show all payments
                        </button>
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  visiblePayments.map(p => ({ ...p, _empName: getEmployeeName(p.employee_id) })).sort(applySortFn(sort)).map((payment) => (
                    <TableRow
                      key={payment.payment_id}
                      data-testid="payment-row"
                      className={payment.period_review_needed ? "bg-amber-50/50" : ""}
                    >
                      <TableCell className="font-medium text-[#0A192F]">
                        <span className="flex items-center gap-1.5">
                          {getEmployeeName(payment.employee_id)}
                          {isTerminated(payment.employee_id) && <UserX className="w-3.5 h-3.5 text-[#E11D48]" title="Terminated" />}
                        </span>
                      </TableCell>
                      <TableCell className="text-slate-600">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          {formatDate(payment.payment_date)}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">
                        <div className="flex items-center gap-2">
                          <span>{formatDate(payment.pay_period_start)} - {formatDate(payment.pay_period_end)}</span>
                          {payment.period_review_needed && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded"
                              title="Pay period doesn't align with this employee's pay group. Edit it, or click the check to mark as reviewed."
                              data-testid={`needs-review-badge-${payment.payment_id}`}
                            >
                              <AlertTriangle className="w-3 h-3" /> Needs review
                            </span>
                          )}
                          {payment.period_snapped_from && (
                            <span
                              className="text-[10px] text-slate-400 italic"
                              title={`Snapped from ${payment.period_snapped_from.start} → ${payment.period_snapped_from.end}`}
                            >
                              (snapped)
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="capitalize">{payment.payment_method}</TableCell>
                      <TableCell className="font-semibold text-[#10B981]">
                        {formatCurrency(payment.amount_paid)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex flex-col gap-1">
                            {payment.extra_amount > 0 && (
                              <span className="text-sm text-[#10B981]">
                                +{formatCurrency(payment.extra_amount)}
                                {payment.extra_reason && <span className="text-slate-400 ml-1">({payment.extra_reason})</span>}
                              </span>
                            )}
                            {payment.deduction_amount > 0 && (
                              <span className="text-sm text-[#E11D48]">
                                -{formatCurrency(payment.deduction_amount)}
                                {payment.deduction_reason && <span className="text-slate-400 ml-1">({payment.deduction_reason})</span>}
                              </span>
                            )}
                            {!payment.extra_amount && !payment.deduction_amount && "-"}
                          </div>
                          {payment.period_review_needed && (
                            <div className="flex flex-col gap-1 shrink-0">
                              <button
                                type="button"
                                onClick={() => openEditPeriod(payment)}
                                className="text-[11px] inline-flex items-center justify-center gap-1 px-2 py-1 rounded border border-[#0D9488] text-[#0D9488] hover:bg-teal-50 whitespace-nowrap"
                                data-testid={`fix-period-${payment.payment_id}`}
                                title="Pick the correct pay period from a dropdown"
                              >
                                <Wand2 className="w-3 h-3" /> Fix period
                              </button>
                              <button
                                type="button"
                                onClick={() => handleClearReviewFlag(payment.payment_id)}
                                className="text-[11px] inline-flex items-center justify-center gap-1 px-2 py-1 rounded border border-amber-200 text-amber-700 hover:bg-amber-50 whitespace-nowrap"
                                data-testid={`clear-review-${payment.payment_id}`}
                                title="Mark this payment's period as correct without changing it"
                              >
                                <CheckCircle2 className="w-3 h-3" /> Reviewed
                              </button>
                            </div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <PayGroupSummarySheet
          open={showSummarySheet}
          onOpenChange={setShowSummarySheet}
        />

        {/* Snap-periods PRE-RUN confirmation dialog (replaces window.confirm) */}
        <Dialog open={showSnapConfirm} onOpenChange={setShowSnapConfirm}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Wand2 className="w-5 h-5 text-[#0D9488]" />
                Snap Payment Periods
              </DialogTitle>
              <DialogDescription>
                One-time tenant-wide cleanup. Safe to run multiple times.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2 text-sm">
              <div className="p-3 bg-slate-50 rounded-md border border-slate-200 space-y-2">
                <p className="text-slate-700">
                  We&apos;ll scan every payment and compare its <span className="font-semibold">pay period</span> to the canonical periods for that employee&apos;s pay group.
                </p>
                <ul className="text-xs text-slate-600 space-y-1 pl-4 list-disc">
                  <li><span className="font-medium text-emerald-700">Within ±3 days</span> → silently snapped to the matching period.</li>
                  <li><span className="font-medium text-amber-700">More than 3 days off</span> → flagged for your manual review (no data changed).</li>
                  <li><span className="font-medium text-slate-500">Already aligned</span> → untouched.</li>
                </ul>
              </div>
              <p className="text-xs text-slate-500">
                Flagged payments will show a <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded"><AlertTriangle className="w-3 h-3" /> Needs review</span> badge here on the Payments table, and the filter chip above will let you jump to them.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSnapConfirm(false)} disabled={snapRunning}>
                Cancel
              </Button>
              <Button
                onClick={handleRunSnapPeriods}
                disabled={snapRunning}
                className="bg-[#0D9488] hover:bg-[#14B8A6]"
                data-testid="snap-confirm-run-btn"
              >
                {snapRunning ? "Scanning…" : "Run scan"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Snap-periods POST-RUN result dialog (replaces toast.success) */}
        <Dialog open={!!snapResult} onOpenChange={(o) => { if (!o) setSnapResult(null); }}>
          <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                Snap complete
              </DialogTitle>
              <DialogDescription>
                Here&apos;s what changed and what still needs your review.
              </DialogDescription>
            </DialogHeader>
            {snapResult && (
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200 text-center">
                    <div className="text-2xl font-bold text-emerald-700" data-testid="snap-result-snapped">{snapResult.snapped}</div>
                    <div className="text-[11px] uppercase text-emerald-700 mt-0.5">Snapped</div>
                  </div>
                  <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-center">
                    <div className="text-2xl font-bold text-amber-700" data-testid="snap-result-flagged">{snapResult.flagged}</div>
                    <div className="text-[11px] uppercase text-amber-700 mt-0.5">Needs review</div>
                  </div>
                  <div className="p-3 rounded-md bg-slate-50 border border-slate-200 text-center">
                    <div className="text-2xl font-bold text-slate-600" data-testid="snap-result-aligned">{snapResult.already_aligned}</div>
                    <div className="text-[11px] uppercase text-slate-500 mt-0.5">Already aligned</div>
                  </div>
                </div>

                {snapResult.snapped > 0 && snapResult.examples_snapped?.length > 0 && (
                  <details className="border border-emerald-200 rounded-md">
                    <summary className="bg-emerald-50 px-3 py-2 font-semibold text-emerald-800 cursor-pointer text-sm">
                      Examples of what was snapped ({snapResult.examples_snapped.length})
                    </summary>
                    <div className="divide-y divide-emerald-100 text-xs">
                      {snapResult.examples_snapped.map((ex, i) => (
                        <div key={i} className="px-3 py-2">
                          <div className="font-medium">{ex.employee}</div>
                          <div className="text-slate-500">
                            <span className="line-through text-red-400">{ex.from}</span>
                            {" → "}
                            <span className="text-emerald-700 font-medium">{ex.to}</span>
                            <span className="ml-2 text-slate-400">(±{ex.diff_days}d)</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </details>
                )}

                {snapResult.flagged > 0 ? (
                  <div className="border border-amber-200 rounded-md overflow-hidden">
                    <div className="bg-amber-50 px-3 py-2 font-semibold text-amber-800 text-sm flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" /> Where to find flagged payments
                    </div>
                    <div className="px-3 py-3 text-xs text-amber-900 space-y-2">
                      <p>
                        Each flagged payment now has a <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded"><AlertTriangle className="w-3 h-3" /> Needs review</span> badge on its Period column. The orange chip above the table will be there too — click it to filter the list down to just the flagged ones.
                      </p>
                      <p>To resolve a flag, either:</p>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>Edit the payment&apos;s pay period to match a real one (auto-clears the flag), or</li>
                        <li>Click the <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded border border-amber-200 text-amber-700"><CheckCircle2 className="w-3 h-3" /> Reviewed</span> button on the row to accept it as-is.</li>
                      </ul>
                    </div>
                    {snapResult.examples_flagged?.length > 0 && (
                      <details className="border-t border-amber-200">
                        <summary className="px-3 py-2 cursor-pointer text-xs font-medium text-amber-800 bg-amber-50/60">
                          First {snapResult.examples_flagged.length} flagged
                        </summary>
                        <div className="divide-y divide-amber-100 text-xs">
                          {snapResult.examples_flagged.map((ex, i) => (
                            <div key={i} className="px-3 py-2 flex justify-between">
                              <span>
                                <span className="font-medium">{ex.employee}</span>
                                <span className="text-slate-500 ml-2">{ex.period}</span>
                              </span>
                              <span className="text-amber-700">{formatCurrency(ex.amount)}</span>
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                ) : (
                  <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200 text-sm text-emerald-800">
                    ✓ Nothing needs manual review — every payment is now aligned.
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              {snapResult?.flagged > 0 && (
                <Button
                  variant="outline"
                  onClick={() => { setFilterReviewOnly(true); setSnapResult(null); }}
                  data-testid="snap-result-jump-btn"
                >
                  <AlertTriangle className="w-4 h-4 mr-1.5" /> Show flagged
                </Button>
              )}
              <Button onClick={() => setSnapResult(null)} className="bg-[#0D9488] hover:bg-[#14B8A6]">
                Done
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* Inline Fix Period dialog — opens from flagged-payment rows */}
        <Dialog open={!!editPeriodTarget} onOpenChange={(o) => { if (!o) setEditPeriodTarget(null); }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Wand2 className="w-5 h-5 text-[#0D9488]" />
                Fix Pay Period
              </DialogTitle>
              <DialogDescription>
                Pick the correct pay period for this payment. The flag will clear automatically when you save.
              </DialogDescription>
            </DialogHeader>
            {editPeriodTarget && (
              <div className="space-y-4 py-2">
                <div className="p-3 bg-slate-50 rounded-md border border-slate-200 text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Employee</span>
                    <span className="font-medium">{getEmployeeName(editPeriodTarget.employee_id)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Amount</span>
                    <span className="font-medium text-[#10B981]">{formatCurrency(editPeriodTarget.amount_paid)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Payment date</span>
                    <span>{formatDate(editPeriodTarget.payment_date)}</span>
                  </div>
                  <div className="flex justify-between text-amber-700 pt-1 border-t border-slate-200 mt-2">
                    <span>Current period (flagged)</span>
                    <span className="line-through">
                      {formatDate(editPeriodTarget.pay_period_start)} – {formatDate(editPeriodTarget.pay_period_end)}
                    </span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>New Pay Period *</Label>
                  {editPeriodLoading ? (
                    <div className="text-xs text-slate-500 py-2">Loading available periods…</div>
                  ) : editPeriodOptions.length > 0 ? (
                    <Select value={editPeriodValue} onValueChange={setEditPeriodValue}>
                      <SelectTrigger data-testid="fix-period-select">
                        <SelectValue placeholder="Select a pay period" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {editPeriodOptions.slice().reverse().map((p) => {
                          const key = `${p.period_start}|${p.period_end}`;
                          const isPaid = p.balance <= 0.01;
                          return (
                            <SelectItem key={key} value={key}>
                              <span className="flex items-center justify-between gap-3 w-full">
                                <span className="font-medium">{formatDate(p.period_start)} – {formatDate(p.period_end)}</span>
                                <span className={`text-xs ${isPaid ? "text-slate-400" : "text-amber-600 font-semibold"}`}>
                                  {isPaid ? "Paid" : `${formatCurrency(p.balance)} unpaid`}
                                </span>
                              </span>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="text-xs text-amber-700 py-2">
                      No canonical periods exist for this employee yet — assign them to a pay group first.
                    </div>
                  )}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditPeriodTarget(null)} disabled={editPeriodSaving}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveEditPeriod}
                disabled={editPeriodSaving || !editPeriodValue}
                className="bg-[#0D9488] hover:bg-[#14B8A6]"
                data-testid="fix-period-save-btn"
              >
                {editPeriodSaving ? "Saving…" : "Save & clear flag"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Payments;
